import { reject as _reject } from 'lodash-es';
import { useMemo } from 'react';
import Link from 'next/link';
import ListIcon from '@mui/icons-material/List';
import TodayIcon from '@mui/icons-material/Today';
import { makeStyles } from 'tss-react/mui';
import Typography from '@mui/material/Typography';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import FilterBar, { type CalendarFilters } from '../calendar/filter-bar';
import { filterableRoute } from '../../utils/routes';
import type { Group } from '../../types/Group';

const useStyles = makeStyles()((theme) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(2, 0, 2, 0),
    [theme.breakpoints.down('sm')]: {
      alignItems: 'flex-start',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
    },
  },
  headerLeft: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    margin: 0,
    paddingRight: theme.spacing(2),
  },
  titleLink: {
    color: 'rgba(0, 0, 0, 0.87)',
    textDecoration: 'none',
  },
}));

type Props = {
  title: React.ReactNode;
  tab?: string;
  filters?: CalendarFilters;
  group: Group;
};

const CalendarHeader = ({ group, title, tab, filters }: Props) => {
  const { classes } = useStyles();

  const basePath = useMemo(() => {
    if (tab === 'upcoming' || tab === 'past' || !tab) {
      return '/calendar';
    }
    return '/calendar/calendar';
  }, [tab]);

  const filterableFilters = useMemo(() => _reject(filters, 'filter'), [filters]);

  return (
    <div>
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <Typography variant="h1" className={classes.title}>
            {title}
          </Typography>
        </div>

        <ToggleButtonGroup
          value={tab || 'upcoming'}
          exclusive
          size="small"
        >
          <ToggleButton
            value="upcoming"
            component={Link}
            href={`/groups/${group.slug}/calendar`}
          >
            <ListIcon sx={{ mr: 0.5 }} />
            Upcoming ({group.upcomingEventsCount})
          </ToggleButton>
          <ToggleButton
            value="past"
            component={Link}
            href={`/groups/${group.slug}/calendar/past`}
          >
            <ListIcon sx={{ mr: 0.5 }} />
            Past ({group.pastEventsCount})
          </ToggleButton>
          <ToggleButton
            value="calendar"
            component={Link}
            href={filterableRoute(`/groups/${group.slug}/calendar/grid`, filterableFilters)}
          >
            <TodayIcon sx={{ mr: 0.5 }} />
            Calendar
          </ToggleButton>
        </ToggleButtonGroup>
      </div>
      {filters && (
        <div>
          <FilterBar
            tab={tab}
            basePath={basePath}
            baseHref="/groups/[slug]"
            baseAs={`/groups/${group.slug}`}
            filters={filters}
          />
        </div>
      )}
    </div>
  );
};

export default CalendarHeader;
